import { Link } from 'react-router-dom';
import { DocSection, Callout, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'analyze-faces', label: 'Analyze faces' },
  { id: 'name-person', label: 'Name a person' },
  { id: 'search', label: 'Search for them' },
  { id: 'multi-person', label: 'Multi-person search' },
  { id: 'merge', label: 'Merge groups & review matching faces' },
  { id: 'ignore', label: "Ignore faces you don't want" },
];

export default function Body() {
  return (
    <>
      <DocSection id="analyze-faces" title="Analyze faces">
        <p>
          Name a face once and ClipRadar finds that person in every clip from then on. Start on
          the <Link to="/docs/interface/media">Media</Link> tab: select the files you want checked
          for faces, right-click, and choose <strong>Find Faces</strong> — or run{' '}
          <strong>Analyze All (visual + speech + faces)</strong> to run every pass at once. Faces
          run on your GPU alongside visual and speech analysis.
        </p>
        <DocShot
          slug="find-and-name-people"
          shot="find-faces"
          alt="The row context menu on selected Media rows, showing Find Faces"
          caption="Right-click one or more selected rows on Media and choose Find Faces."
        />
        <p>
          Once it finishes, switch to the <Link to="/docs/interface/people">People</Link> tab —
          every face ClipRadar detected is grouped into clusters under{' '}
          <strong>Unnamed People</strong>, ready to name.
        </p>
      </DocSection>

      <DocSection id="name-person" title="Name a person">
        <p>
          Each cluster card under <strong>Unnamed People</strong> shows a couple of face
          thumbnails and a count. Type a name into its <strong>Add name&hellip;</strong> field and
          press Enter (or click away) — ClipRadar turns that cluster into a named{' '}
          <strong>person</strong>. If a person with that name already exists, the cluster folds
          into them instead of creating a duplicate.
        </p>
        <DocShot slug="find-and-name-people" shot="name-cluster" alt="Typing a name into a cluster card's Add name field" />
        <p>
          A named cluster moves to <strong>Named People</strong>, where you can rename it again at
          any time from the card.
        </p>
      </DocSection>

      <DocSection id="search" title="Search for them">
        <p>
          Back on <Link to="/docs/interface/search">Search</Link>, start typing a person&rsquo;s name
          in the search box — ClipRadar suggests matching named people in a list as you type, and
          picking one adds them to your query as a pill. (Typing <span style={kbd}>@</span> first is
          the explicit way to pull up that same list.) Use a person alone or combined with an ordinary
          description: <span style={kbd}>Sarah</span> finds every clip she&rsquo;s in, and{' '}
          <span style={kbd}>Sarah close-up</span> narrows that to close-ups of her specifically. The
          People filter next to the search box does the same thing.
        </p>
        <DocShot slug="find-and-name-people" shot="people-search" alt="Search results filtered to one named person" />
      </DocSection>

      <DocSection id="multi-person" title="Multi-person search">
        <p>
          Add more than one person — another <span style={kbd}>@</span>-mention, or another pick
          in the People filter — to bring more than one specific person into the same search, for
          example <span style={kbd}>@Sarah @Marcus</span>. Combine with an ordinary description to
          narrow the results further.
        </p>
      </DocSection>

      <DocSection id="merge" title="Merge groups & review matching faces">
        <p>
          Face clustering isn&rsquo;t perfect — the same person can split across a couple of
          clusters depending on lighting or angle. Two tools fix that without you hunting down
          duplicates:
        </p>
        <ul>
          <li>
            <strong>Review matching faces</strong> — on a named person&rsquo;s card menu, this
            looks for unnamed clusters that probably belong to them and puts them in a review
            banner for you to accept or reject one by one.
          </li>
          <li>
            <strong>Merge&hellip;</strong> — select two or more cards yourself (people or clusters)
            and fold them together when you can already tell it&rsquo;s the same person.
          </li>
        </ul>
        <Callout kind="tip">
          Merging is the riskier move: accepting a wrong match mixes two people&rsquo;s faces
          together, while leaving a real split unmerged only costs you a gap in that person&rsquo;s
          search coverage. When in doubt, use <strong>Review matching faces</strong> and check
          each suggestion rather than merging broadly.
        </Callout>
        <p>
          See <Link to="/docs/concepts/people-and-faces">People &amp; faces</Link> for why splits
          happen in the first place.
        </p>
      </DocSection>

      <DocSection id="ignore" title="Ignore faces you don't want">
        <p>
          For faces you never want clustered — background extras, a stock clip, anyone you
          don&rsquo;t want ClipRadar tracking — use <strong>Ignore permanently&hellip;</strong> on a
          cluster card&rsquo;s menu. Unlike <strong>Delete&hellip;</strong>, which just dismisses a
          cluster card (those faces can regroup and reappear later), Ignore blacklists the
          underlying faces so they stop showing up at all. Changed your mind?{' '}
          <strong>Reset ignored faces</strong>, in the People board header, brings them all back
          for re-clustering.
        </p>
      </DocSection>
    </>
  );
}
